import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useFunnelCart } from '../../context/FunnelCartProvider';
import {
  getSkuById,
  getSkuServiceCategories,
} from '../../lib/pricebook/pricebook';
import OrderBreakdown from './OrderBreakdown';
import { gradientPillCss } from '../../styles/gradientPill';
import type { CalculatedOrderLineItem } from '../../lib/pricing/calcOrder';
import {
  cyberGlassBackdrop,
} from '../../styles/cyberGlass';

const Backdrop = styled.button<{ $open: boolean }>`
  border: 0;
  margin: 0;
  padding: 0;
  position: fixed;
  inset: 0;
  ${cyberGlassBackdrop};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
  z-index: 90;
`;

const Panel = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: min(460px, 100vw);
  height: 100dvh;
  min-height: 100vh;
  background: rgba(19, 27, 39, 0.7);
  color: ${({ theme }) => theme.colors.gray900};
  transform: translateX(${({ $open }) => ($open ? '0' : '102%')});
  transition: transform 0.22s ease;
  z-index: 100;
  display: grid;
  grid-template-rows: auto 1fr auto;
  backdrop-filter: blur(26px) saturate(145%);
  -webkit-backdrop-filter: blur(26px) saturate(145%);

  @media (max-width: 720px) {
    inset: 0;
    width: 100vw;
  }
`;

const Header = styled.div`
  padding:
    calc(env(safe-area-inset-top) + 1rem)
    1rem
    0.8rem;
  display: grid;
  gap: 0.65rem;
`;

const Scroll = styled.div`
  padding: 0 1rem 1rem;
  overflow: auto;
  min-height: 0;
  display: grid;
  align-content: start;
  gap: 0.85rem;
`;

const Footer = styled.div`
  padding:
    0
    1rem
    calc(env(safe-area-inset-bottom) + 1rem);
  display: grid;
  gap: 0.65rem;

  > * {
    width: 100%;
  }
`;

const GhostBtn = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.08);
  color: ${({ theme }) => theme.colors.gray900};
  border-radius: 999px;
  min-height: 44px;
  padding: 0.55rem 0.85rem;
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
`;

const SolidLink = styled(Link)`
  ${gradientPillCss};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-family: var(--font-tech);
  font-size: clamp(1.2rem, 2.8vw, 1.55rem);
  line-height: 0.92;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const HeaderMeta = styled.p`
  margin: 0;
  color: rgba(226, 232, 240, 0.76);
  font-size: 0.92rem;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  justify-self: end;
  width: auto;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.gray900};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-height: 40px;
  padding: 0.25rem;
  cursor: pointer;
`;

const EmptyState = styled.p`
  margin: 0;
  color: rgba(226, 232, 240, 0.82);
  line-height: 1.5;
`;

export default function CartDrawer() {
  const navigate = useNavigate();
  const {
    isDrawerOpen,
    closeDrawer,
    order,
    removeSku,
    clearCart,
    totalQuantity,
    primaryCategories,
  } = useFunnelCart();
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(max-width: 1080px)');
    const syncViewport = () => setIsMobileViewport(mediaQuery.matches);

    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);

    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDrawerOpen]);

  const handleSelectLineItem = (item: CalculatedOrderLineItem) => {
    const sku = getSkuById(item.skuId);
    const categories = getSkuServiceCategories(sku);
    const preferredCategory =
      primaryCategories.find((category) => categories.includes(category)) ?? categories[0];
    const servicePath =
      preferredCategory === 'platform'
        ? '/services/platform-development'
        : preferredCategory === 'marketing'
          ? '/services/managed-marketing'
          : preferredCategory === 'assets_design'
            ? '/services/assets-design'
            : null;

    if (!servicePath) return;

    closeDrawer();
    navigate(`${servicePath}?mobileSnapshot=open`);
  };

  return (
    <>
      <Backdrop
        type="button"
        aria-label="Close cart"
        $open={isDrawerOpen}
        onClick={closeDrawer}
      />
      <Panel $open={isDrawerOpen} aria-hidden={!isDrawerOpen} role="dialog" aria-modal="true">
        <Header>
          <CloseButton type="button" onClick={closeDrawer} aria-label="Close cart">
            <X size={18} strokeWidth={2.2} />
          </CloseButton>
          <HeaderTitle>Cart ({totalQuantity})</HeaderTitle>
          <HeaderMeta>
            {isMobileViewport
              ? 'Tap a service in your cart to reopen its snapshot before checkout.'
              : 'Review your selected package items before continuing to checkout.'}
          </HeaderMeta>
        </Header>
        <Scroll>
          {order.lineItems.length ? (
            <OrderBreakdown
              order={order}
              onRemoveSku={removeSku}
              onSelectLineItem={isMobileViewport ? handleSelectLineItem : undefined}
              hidePrices
              flat
              dashedItems
            />
          ) : (
            <EmptyState>Your package is empty.</EmptyState>
          )}
        </Scroll>
        <Footer>
          <GhostBtn
            type="button"
            onClick={() => {
              clearCart();
              closeDrawer();
            }}
          >
            Clear cart
          </GhostBtn>
          <SolidLink to="/checkout" onClick={closeDrawer}>
            Continue
          </SolidLink>
        </Footer>
      </Panel>
    </>
  );
}
